import { Bot, Loader2, MessageSquare, User } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import apiService from "../../shared/apiService";
import { API_ENDPOINTS } from "../../shared/constants";
import Modal from "../ui/Modal";

interface Comment {
  id: string;
  text?: string;
  message?: string; // Facebook usa 'message'
  username?: string;
  author?: string; // LinkedIn
  from?: { name?: string }; // Facebook
  timestamp?: number;
  created_time?: string;
  like_count?: number;
}

interface CommentsListProps {
  platform: "instagram" | "facebook" | "linkedin";
  postId: string; // ID do post na plataforma
  postUrn?: string; // URN para LinkedIn (se necessário)
  onReply?: (commentId: string, commentText: string) => void;
  className?: string;
}

const CommentsList: React.FC<CommentsListProps> = ({
  platform,
  postId,
  postUrn,
  onReply,
  className = "",
}) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorInfo, setErrorInfo] = useState<{
    message: string;
    actionLabel?: string;
    actionHref?: string;
  } | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);

  const loadComments = useCallback(async () => {
    if (!postId && !postUrn) {
      setComments([]);
      return;
    }
    setLoading(true);
    setErrorInfo(null);

    try {
      let url = "";
      let params: Record<string, string> | undefined;

      if (platform === "instagram") {
        url = API_ENDPOINTS.SOCIAL.INSTAGRAM_POST_COMMENTS.replace(
          "{id}",
          postId,
        );
      } else if (platform === "facebook") {
        url = API_ENDPOINTS.SOCIAL.FACEBOOK_POST_COMMENTS.replace(
          "{id}",
          postId,
        );
      } else if (platform === "linkedin") {
        // LinkedIn precisa do URN completo
        const urn = postUrn || postId;
        // Usar query parameter para evitar problemas com dois pontos no URN
        url = API_ENDPOINTS.SOCIAL.LINKEDIN_POST_COMMENTS;
        params = { post_urn: urn };
      }

      const data = await apiService.get(url, params);
      if (data?.success === false) {
        const status = data.status || 500;
        const message = data.message || "Erro ao carregar comentários";
        let actionLabel: string | undefined;
        let actionHref: string | undefined;

        if (status === 401 || status === 403) {
          actionLabel = "Reconectar LinkedIn";
          actionHref = "/organization?tab=integrations";
        }

        const error = new Error(message);
        (error as any).actionLabel = actionLabel;
        (error as any).actionHref = actionHref;
        throw error;
      }

      setComments(data.comments || []);
    } catch (err: any) {
      const statusCode = err?.statusCode;
      const needsReconnect = statusCode === 401 || statusCode === 403;
      const friendlyMessage = err?.message || "Erro ao carregar comentários";
      setErrorInfo({
        message: friendlyMessage,
        actionLabel: needsReconnect ? "Reconectar LinkedIn" : err?.actionLabel,
        actionHref: needsReconnect
          ? "/organization?tab=integrations"
          : err?.actionHref,
      });
      console.error("Erro ao carregar comentários:", err);
    } finally {
      setLoading(false);
    }
  }, [platform, postId, postUrn]);

  useEffect(() => {
    if (expanded && !hasFetchedOnce) {
      setHasFetchedOnce(true);
      loadComments();
    }
  }, [expanded, hasFetchedOnce, loadComments]);

  const handleReply = async (commentId: string, commentText: string) => {
    if (!onReply) return;

    setReplyingTo(commentId);
    try {
      onReply(commentId, commentText);
      await loadComments();
    } catch (err) {
      console.error("Erro ao responder:", err);
    } finally {
      setReplyingTo(null);
    }
  };

  const getCommentText = (comment: Comment): string => {
    return comment.text || comment.message || "";
  };

  const getCommentAuthor = (comment: Comment): string => {
    if (platform === "facebook" && comment.from) {
      return comment.from.name || "Usuário";
    }
    if (platform === "linkedin" && comment.author) {
      return comment.author.split(":").pop() || "Usuário";
    }
    return comment.username || "Usuário";
  };

  const formatDate = (timestamp?: number, dateString?: string): string => {
    if (dateString) {
      try {
        return new Date(dateString).toLocaleString("pt-BR");
      } catch {
        return "";
      }
    }
    if (timestamp) {
      try {
        return new Date(timestamp).toLocaleString("pt-BR");
      } catch {
        return "";
      }
    }
    return "";
  };

  return (
    <div className={className}>
      <button
        onClick={() => setExpanded(true)}
        className="flex items-center justify-center gap-2 w-full h-9 px-3 text-sm font-medium text-purple-600 hover:text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg border border-purple-200 transition-colors whitespace-nowrap"
      >
        <MessageSquare className="w-4 h-4 shrink-0" />
        <span>Ver comentários</span>
      </button>

      {expanded && (
        <Modal
          open={expanded}
          onClose={() => setExpanded(false)}
          title="Comentários"
          subtitle={platform ? `Comentários do ${platform}` : undefined}
          maxWidth="max-w-2xl"
          headerVariant="gradient"
        >
          <div className="space-y-4 p-2 min-h-[200px]">
            <div className="flex items-center justify-end">
              <button
                onClick={loadComments}
                disabled={loading}
                className="text-sm px-4 py-2 text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 rounded-lg font-medium transition-colors"
              >
                {loading ? "Carregando..." : "Atualizar"}
              </button>
            </div>

            {loading && comments.length === 0 && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
                <span className="ml-3 text-sm text-gray-600">
                  Carregando comentários...
                </span>
              </div>
            )}

            {errorInfo && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <p className="text-sm text-red-800">{errorInfo.message}</p>
                {errorInfo.actionHref && (
                  <div className="mt-3">
                    <a
                      href={errorInfo.actionHref}
                      className="inline-flex items-center px-4 py-2 text-xs font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
                    >
                      {errorInfo.actionLabel || "Ver configurações"}
                    </a>
                  </div>
                )}
              </div>
            )}

            {!loading && comments.length === 0 && !errorInfo && (
              <div className="text-center py-10 text-gray-500 bg-gray-50 rounded-xl border border-gray-100">
                Nenhum comentário ainda
              </div>
            )}

            {comments.length > 0 && (
              <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-2 custom-scrollbar">
                {comments.map((comment) => (
                  <div
                    key={comment.id}
                    className="bg-white border border-gray-200 rounded-xl p-4 shadow-xs hover:border-purple-200 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="p-2 bg-purple-100 rounded-full">
                          <User className="w-4 h-4 text-purple-600" />
                        </div>
                        <span className="text-sm font-semibold text-gray-900">
                          {getCommentAuthor(comment)}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 font-medium bg-gray-100 px-2 py-1 rounded-md">
                        {formatDate(comment.timestamp, comment.created_time)}
                      </div>
                    </div>

                    <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                      {getCommentText(comment)}
                    </p>

                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      {comment.like_count !== undefined &&
                      comment.like_count > 0 ? (
                        <div className="text-xs text-gray-500 flex items-center gap-1 font-medium">
                          <span>👍</span>
                          <span>{comment.like_count} curtida(s)</span>
                        </div>
                      ) : (
                        <div />
                      )}
                      {onReply && (
                        <button
                          onClick={() =>
                            handleReply(comment.id, getCommentText(comment))
                          }
                          disabled={replyingTo === comment.id}
                          className="flex items-center gap-1.5 text-xs font-semibold text-purple-600 hover:text-purple-700 disabled:opacity-50 transition-colors bg-purple-50 px-3 py-1.5 rounded-lg"
                        >
                          {replyingTo === comment.id ? (
                            <>
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              <span>Respondendo...</span>
                            </>
                          ) : (
                            <>
                              <Bot className="w-3.5 h-3.5" />
                              <span>Responder com IA</span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default CommentsList;
